import React, { useEffect, useState, useRef, useContext } from 'react';
import { Context } from '../../../App';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import InputAdornment from '@mui/material/InputAdornment';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MuiSnackbar from '../../common/MuiSnackbar';
import CircularProgress from '@mui/material/CircularProgress';
import SeoHelmet from '../../common/SeoHelmet';
import CalculateButton from '../../common/calculateButton';
import SaveButton from '../../common/saveButton';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Button from '@mui/material/Button';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import Sidebar from '../../sidebar/Sidebar';
import DecisionModal from '../../modals/DecisionModal';
import PricingInfoModal from '../../modals/PricingInfoModal';
import MeyerhofResult from './/MeyerhofResult';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import MeyerhofSketch from './MeyerhofSketch';
import { useCalculationContext, CalculationContextProvider } from '../../context/calculationContext';
import useLoadSavedCalculation from '../useLoadSavedCalculation';
import { svgContainerToPngBase64 } from '../../../helpers/svgContainerToPngBase64';

const Meyerhof = () => {
  const { user } = useContext(Context);

  const {
    MAX_NAME_LENGTH,
    isLoadingCalculate,
    isLoadingLoadAll,
    isLoadingLoad,
    isLoadingSave,
    result,
    calculations,
    showOutput,
    setShowOutput,
    calculateButtonDisabled,
    setCalculateButtonDisabled,
    saveButtonDisabled,
    setSaveButtonDisabled,
    errorMessage,
    displaySidebar,
    setDisplaySidebar,
    decisionModalIsOpen,
    decision,
    setDecision,
    handleSidebarDisplay,
    setDecisionModalIsOpenToFalse,
    showMuiSnack,
    open,
    setOpen,
    alertMessage,
    severity,
    onChangeWarningForPositiveDecimals,
    calculate,
    loadOneCalculation,
    loadAllCalculations,
    update,
    checkCalculationNameforSaving,
    checkCalculationNameforSavingAs,
    deleteCalculations,
    calculationCountReached,
    saveCountReached,
  } = useCalculationContext();

  //SET STATES FOR INPUT VARIABLES
  const [name, setName] = useState('');
  const [cohesion, setCohesion] = useState('');
  const [phi, setPhi] = useState('');
  const [df, setDf] = useState('');
  const [fndWidth, setFndWidth] = useState('');
  const [fndLength, setFndLength] = useState('');
  const [theta, setTheta] = useState('');
  const [gamma, setGamma] = useState('');
  const [gwt, setGwt] = useState('');
  const [FsSt, setFsSt] = useState('');
  const [FsSs, setFsSs] = useState('');
  const [FSt, setFSt] = useState('');
  const [FSs, setFSs] = useState('');

  //PDF RELATED STATES
  const [isExporting, setIsExporting] = useState(false);
  const [showPdfWarningModal, setShowPdfWarningModal] = useState(false);
  const sketchContainerRef = useRef(null);

  // SET THE LOADED INPUTS
  const setInputs = (inputs) => {
    setName(inputs.name);
    setCohesion(inputs.cohesion);
    setPhi(inputs.phi);
    setDf(inputs.df);
    setFndWidth(inputs.fndWidth);
    setFndLength(inputs.fndLength);
    setTheta(inputs.theta);
    setGamma(inputs.gamma);
    setGwt(inputs.gwt);
    setFsSt(inputs.FsSt);
    setFsSs(inputs.FsSs);
    setFSt(inputs.FSt);
    setFSs(inputs.FSs);
  };

  //INPUTS FOR CALCULATION
  const calculationInput = {
    name,
    cohesion,
    phi,
    df,
    fndWidth,
    fndLength,
    theta,
    gamma,
    gwt,
    FsSt,
    FsSs,
    FSt,
    FSs,
  };

  // MANAGE SAVE & CALCULATE BUTTONS DISABLED/ENABLED STATES
  useEffect(() => {
    setCalculateButtonDisabled(false);
    setSaveButtonDisabled(false);

    if (
      (calculationInput.name || calculationInput.name === 0) &&
      (calculationInput.cohesion || calculationInput.cohesion === 0) &&
      (calculationInput.phi || calculationInput.phi === 0) &&
      (calculationInput.df || calculationInput.df === 0) &&
      (calculationInput.fndWidth || calculationInput.fndWidth === 0) &&
      (calculationInput.fndLength || calculationInput.fndLength === 0) &&
      (calculationInput.theta || calculationInput.theta === 0) &&
      (calculationInput.gamma || calculationInput.gamma === 0) &&
      (calculationInput.gwt || calculationInput.gwt === 0) &&
      (calculationInput.FsSt || calculationInput.FsSt === 0) &&
      (calculationInput.FsSs || calculationInput.FsSs === 0) &&
      (calculationInput.FSt || calculationInput.FSt === 0) &&
      (calculationInput.FSs || calculationInput.FSs === 0)
    ) {
      setCalculateButtonDisabled(false);
      setSaveButtonDisabled(false);
    } else {
      setCalculateButtonDisabled(true);
      setSaveButtonDisabled(true);
    }
  }, [
    setCalculateButtonDisabled,
    setSaveButtonDisabled,
    calculationInput.name,
    calculationInput.cohesion,
    calculationInput.phi,
    calculationInput.df,
    calculationInput.fndWidth,
    calculationInput.fndLength,
    calculationInput.theta,
    calculationInput.gamma,
    calculationInput.gwt,
    calculationInput.FsSt,
    calculationInput.FsSs,
    calculationInput.FSt,
    calculationInput.FSs,
  ]);

  // DO NOT RENDER OUTPUT IF ANY INPUT PARAMETER EXCEPT NAME CHANGES
  useEffect(() => {
    setShowOutput(false);
  }, [
    setShowOutput,
    calculationInput.cohesion,
    calculationInput.phi,
    calculationInput.df,
    calculationInput.fndWidth,
    calculationInput.fndLength,
    calculationInput.theta,
    calculationInput.gamma,
    calculationInput.gwt,
    calculationInput.FsSt,
    calculationInput.FsSs,
    calculationInput.FSt,
    calculationInput.FSs,
  ]);

  //CLEAR INPUTS AND CLEAR OUTPUT
  const createNewCalculation = () => {
    setName('');
    setCohesion('');
    setPhi('');
    setDf('');
    setFndWidth('');
    setFndLength('');
    setTheta('');
    setGamma('');
    setGwt('');
    setFsSt('');
    setFsSs('');
    setFSt('');
    setFSs('');
    setShowOutput(false);
  };

  //API URLS
  const URL = {
    CALCULATE: '/api/v1/calculations/meyerhof',
    LOADONE: `/api/v1/calculations/meyerhof/loadOne/`,
    LOADALL: '/api/v1/calculations/meyerhof/loadAll',
    UPDATE: `/api/v1/calculations/meyerhof/update/`,
    SAVE: '/api/v1/calculations/meyerhof/save',
    CHECKUNIQUENAME: `/api/v1/calculations/meyerhof/checkUniqueName/`,
    DELETE: '/api/v1/calculations/meyerhof/delete',
  };
  useLoadSavedCalculation({ URL, loadOneCalculation, calculate, setInputs, showMuiSnack });

  const handleChangeName = (e) => {
    if (e.target.value.length >= MAX_NAME_LENGTH) {
      showMuiSnack(`Calculation name can be max ${MAX_NAME_LENGTH} characters.`, 'warning');
    }
    setName(e.target.value);
  };

  const onChangeWarningForTheta = (e, variable, variableSetter) => {
    const value = e.target.value.trim();

    // Allow clearing (so user can delete input)
    if (value === '') {
      variableSetter('');
      return;
    }

    // Regex for positive real numbers (allow decimals)
    const regexForPositiveDecimals = /^[+]?\d*(?:[.]\d*)?$/;

    // Validate format first
    if (!regexForPositiveDecimals.test(value)) {
      showMuiSnack(`${variable}: Can only be a positive number.`, 'warning');
      return;
    }

    // Parse and check range (0–90)
    const num = Number(value);
    if (Number.isNaN(num) || num < 0 || num > 90) {
      showMuiSnack(`${variable}: Value must be between 0 and 90.`, 'warning');
      return;
    }

    // ✅ Valid number in range
    variableSetter(value);
  };
  const onChangeWarningForPhi = (e, variable, variableSetter) => {
    const value = e.target.value.trim();

    // Allow clearing (so user can delete input)
    if (value === '') {
      variableSetter('');
      return;
    }

    // Regex for positive real numbers (allow decimals)
    const regexForPositiveDecimals = /^[+]?\d*(?:[.]\d*)?$/;

    // Validate numeric format
    if (!regexForPositiveDecimals.test(value)) {
      showMuiSnack(`${variable}: Can only be a positive number.`, 'warning');
      return;
    }

    // Parse and check range (0–50)
    const num = Number(value);
    if (Number.isNaN(num) || num < 0 || num > 50) {
      showMuiSnack(`${variable}: Value must be between 0 and 50.`, 'warning');
      return;
    }

    // ✅ Valid number in range
    variableSetter(value);
  };

  //TABS
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  //HANDLE PDF
  const handleExportPdf = async () => {
    if (isExporting) return;
    if (user.role === 'basic' || user.role === 'standard') {
      setShowPdfWarningModal(true);
      return;
    }
    setIsExporting(true);
    try {
      const [sketchPngBase64] = await Promise.all([
        svgContainerToPngBase64(sketchContainerRef.current, {
          scale: 2, // just controls resolution
        }),
      ]);

      const payload = {
        ...calculationInput,
        sketchPngBase64,
      };

      const res = await fetch('/api/v1/pdf/meyerhof', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('PDF generation failed');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'geocalc-meyerhof-report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      // optionally show a snackbar
    } finally {
      setIsExporting(false);
    }
  };

  // RETURN INPUT AND OUTPUT JSX
  return (
    <>
      <SeoHelmet
        title="GeoCalc | Bearing Capacity of Shallow Foundations | Meyerhof (1963)"
        description="Calculate bearing capacity of shallow foundations in soil. Meyerhof (1963)."
        url="https://www.geocalc.app/calculations/meyerhof"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="tool"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          {
            name: "Bearing Capacity of Shallow Foundations | Mey­erhof's Method",
            url: 'https://www.geocalc.app/calculations/meyerhof',
          },
        ]}
      />
      <Header />
      <div className="container">
        <MuiSnackbar open={open} autoHideDuration={3000} setOpen={setOpen} severity={severity} alertMessage={alertMessage} />
        {displaySidebar ? (
          <Sidebar
            URL={URL}
            loadAllCalculations={loadAllCalculations}
            calculations={calculations}
            loadOneCalculation={loadOneCalculation}
            calculate={calculate}
            setInputs={setInputs}
            deleteCalculations={deleteCalculations}
            createNewCalculation={createNewCalculation}
            isLoading={isLoadingLoadAll}
            handleSidebarDisplay={handleSidebarDisplay}
            displaySidebar={displaySidebar}
            setDisplaySidebar={setDisplaySidebar}
          />
        ) : (
          <MenuOpenIcon
            color="primary"
            fontSize="large"
            className="sidebar-open-icon"
            alt="side bar menu icon"
            label="Show my calculations"
            onClick={handleSidebarDisplay}
          />
        )}
        <DecisionModal
          URL={URL}
          data={calculationInput}
          decisionModalIsOpen={decisionModalIsOpen}
          setDecisionModalIsOpenToFalse={setDecisionModalIsOpenToFalse}
          decision={decision}
          setDecision={setDecision}
          checkCalculationNameforSavingAs={checkCalculationNameforSavingAs}
          name={name}
          setName={setName}
          errorMessage={errorMessage}
          update={update}
          saveButtonDisabled={saveButtonDisabled}
        />
        <PricingInfoModal open={showPdfWarningModal} setOpen={setShowPdfWarningModal} role={user.role} />

        <div className="right-calculation">
          <div className="meyerhofContainer">
            <h1>Bearing Capacity of Shallow Foundations - Meyerhof (1963)</h1>
            <div className="meyerhofTabContainer">
              <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons allowScrollButtonsMobile>
                <Tab label="Soil Properties" sx={{ textTransform: 'none' }} />
                <Tab label="Foundation Geometry" sx={{ textTransform: 'none' }} />
                <Tab label="Loads" sx={{ textTransform: 'none' }} />
                <Tab label="Factor of Safety" sx={{ textTransform: 'none' }} />
              </Tabs>
            </div>
            {value === 0 && (
              <div className="meyerhofInputs">
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label="Name"
                  variant="outlined"
                  inputProps={{
                    maxLength: `${MAX_NAME_LENGTH}`,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={`Your calculation name (Max. ${MAX_NAME_LENGTH} characters)`}
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={name}
                  onChange={handleChangeName}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label="c (kPa)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Cohesion" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={cohesion}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Cohesion', setCohesion)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label={
                    <span key={'phi'}>
                      &phi; (<sup>o</sup>)
                    </span>
                  }
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title="Internal friction angle — enter a value between 0° and 50°"
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={phi}
                  onChange={(e) => onChangeWarningForPhi(e, 'φ', setPhi)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label={
                    <span key={'gamma'}>
                      &gamma; (kN/m<sup>3</sup>)
                    </span>
                  }
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Natural unit weight of soil" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={gamma}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Gamma', setGamma)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label={'GWT (m)'}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title="Ground water level measured from ground surface"
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={gwt}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'GWT', setGwt)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
              </div>
            )}
            {value === 1 && (
              <div className="meyerhofInputs">
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label={
                    <span key={'df'}>
                      D<sub>f</sub> (m)
                    </span>
                  }
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Depth of foundation" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={df}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Df', setDf)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label={'B (m)'}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Foundation width" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={fndWidth}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Foundation Width', setFndWidth)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label={'L (m)'}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Foundation length" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={fndLength}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Foundation Length', setFndLength)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label={
                    <span key={'theta'}>
                      &theta; (<sup>o</sup>)
                    </span>
                  }
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title="Angle of force — enter a value between 0° and 90°"
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={theta}
                  onChange={(e) => onChangeWarningForTheta(e, 'θ (Angle of Force)', setTheta)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
              </div>
            )}
            {value === 2 && (
              <div className="meyerhofInputs">
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label={'FSt(kPa)'}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Static load" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={FSt}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'FSt', setFSt)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label={'FSs(kPa)'}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Seismic load" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={FSs}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'FSs', setFSs)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
              </div>
            )}
            {value === 3 && (
              <div className="meyerhofInputs">
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label={'FsSt'}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Factor of safety (Static)" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={FsSt}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'FsSt', setFsSt)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label={'FsSs'}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Factor of safety (Seismic)" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={FsSs}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'FsSs', setFsSs)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
              </div>
            )}
            <MeyerhofSketch
              cohesion={cohesion}
              phi={phi}
              theta={theta}
              df={df}
              fndWidth={fndWidth}
              gamma={gamma}
              fndLength={fndLength}
              gwt={gwt}
              FsSt={FsSt}
              FsSs={FsSs}
              FSt={FSt}
              FSs={FSs}
              calculateButtonDisabled={calculateButtonDisabled}
            />
            {/* For PDF Exports */}
            <div
              ref={sketchContainerRef}
              style={{
                position: 'absolute',
                left: -99999,
                top: -99999,
                width: 900,
                height: 600,
                pointerEvents: 'none',
                overflow: 'hidden',
              }}
            >
              <MeyerhofSketch
                cohesion={cohesion}
                phi={phi}
                theta={theta}
                df={df}
                fndWidth={fndWidth}
                gamma={gamma}
                fndLength={fndLength}
                gwt={gwt}
                FsSt={FsSt}
                FsSs={FsSs}
                FSt={FSt}
                FSs={FSs}
                calculateButtonDisabled={calculateButtonDisabled}
                variant={'pdf-light'}
              />
            </div>
            <div className="buttonContainer meyerhofSaveCalculateButtonContainer">
              <CalculateButton
                calculationCountReached={calculationCountReached}
                calculate={calculate}
                calculationInput={calculationInput}
                URL={URL}
                calculateButtonDisabled={calculateButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
              />
              <Button
                variant="contained"
                startIcon={!isExporting ? <PictureAsPdfIcon /> : null}
                onClick={handleExportPdf}
                disabled={!showOutput || isExporting}
                style={{ width: '200px' }}
              >
                {isExporting ? (
                  <>
                    <CircularProgress size={18} style={{ marginRight: 8 }} />
                    Creating PDF…
                  </>
                ) : (
                  'Create PDF'
                )}
              </Button>

              <SaveButton
                saveCountReached={saveCountReached}
                checkCalculationNameforSaving={checkCalculationNameforSaving}
                calculationInput={calculationInput}
                URL={URL}
                saveButtonDisabled={saveButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
              />
            </div>
          </div>

          {showOutput && <MeyerhofResult result={result} />}
          {isLoadingLoad && <CircularProgress size={30} />}
          {isLoadingCalculate && (
            <div className="centerLoading">
              <CircularProgress />
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

const MeyerhofWithProvider = () => {
  return (
    <CalculationContextProvider>
      <Meyerhof />
    </CalculationContextProvider>
  );
};
export default MeyerhofWithProvider;
